import { Suspense } from 'react';
import { ContentJsonLd } from '@/components/content-json-ld';
/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Region } from '@/components/region';
import type { PageWithComponentData } from '@/lib/util/pageLoader.server';
import type { ShopperProducts } from '@/scapi';
import type { EatalyBreadcrumbItem } from '@/components/eataly-breadcrumbs';

export type FullEditorialCategoryPageRouteData = {
    kind: 'fullEditorialCategoryPage';
    category: ShopperProducts.schemas['Category'];
    page: PageWithComponentData;
    categoryId: string;
    pageUrl: string;
    structuredData?: Promise<Record<string, unknown> | null>;
    breadcrumbItems: EatalyBreadcrumbItem[];
    breadcrumbBackgroundClass: string;
    breadcrumbHomeTo?: string;
    breadcrumbMergeHomeWithFirstItem?: boolean;
    primaryCategoryPath: string | null;
    seoSiteName: string;
};

export function FullEditorialCategoryPageRoute({ data }: { data: FullEditorialCategoryPageRouteData }) {
    return (
        <>
            {data.structuredData && (
                <Suspense fallback={null}>
                    <ContentJsonLd promise={data.structuredData} />
                </Suspense>
            )}
            {data.page.regions?.map((region) => (
                <Region key={`${data.categoryId}:${region.id}`} page={data.page} regionId={region.id} />
            ))}
        </>
    );
}
